import React from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { getAreaGuideBySlug } from "src/data/areaGuides";

const AreaGuideDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? getAreaGuideBySlug(slug) : undefined;

  if (!guide) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 px-6">
        <h1 className="page-section-heading">Area guide not found</h1>
        <Link
          to="/guides/areas"
          className="font-teko text-sm uppercase text-brand-gold hover:text-text-obsidian"
        >
          ← Back to area guides
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {guide.title} Area Guide | Letting Engine
        </title>
        <meta
          name="description"
          content={`${guide.tagline} — transport, highlights, and rental insights for ${guide.title}.`}
        />
      </Helmet>

      <section className="pt-40 pb-16 bg-bg-porcelain">
        <div className="max-w-[900px] mx-auto px-[5%]">
          <Link
            to="/guides/areas"
            className="font-manrope text-sm text-text-slate hover:text-text-obsidian mb-6 inline-block"
          >
            ← All area guides
          </Link>
          <span className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2">
            {guide.tagline}
          </span>
          <h1 className="font-teko text-4xl lg:text-5xl text-text-obsidian mb-4">
            {guide.title}
          </h1>
          <div className="w-16 h-1.5 bg-brand-yellow mb-8" />
        </div>
      </section>

      <section className="py-16 bg-bg-white">
        <div className="max-w-[900px] mx-auto px-[5%] space-y-16">
          <div>
            <h2 className="section-title">Overview</h2>
            <div className="space-y-4">
              {guide.overview.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 40)}
                  className="font-manrope text-base text-text-slate leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <div>
            <h2 className="section-title">Highlights</h2>
            <div className="flex flex-wrap gap-2">
              {guide.highlights.map((item) => (
                <span
                  key={item}
                  className="font-manrope text-sm px-3 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)] text-text-obsidian"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h2 className="section-title">Getting around</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {guide.transport.map((line) => (
                <li
                  key={line}
                  className="font-manrope text-sm text-text-slate flex items-center gap-2"
                >
                  <span className="w-2 h-2 bg-brand-yellow rounded-full shrink-0" />
                  {line}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="section-title">Renting here</h2>
            <p className="font-manrope text-base text-text-slate leading-relaxed">
              {guide.rentalSnapshot}
            </p>
          </div>

          <div className="pt-8 border-t border-t-solid border-t-[rgba(0,0,0,0.08)]">
            <Link
              to="/services/rent/properties"
              className="inline-block px-8 py-3 bg-brand-yellow text-text-obsidian font-teko text-base uppercase tracking-wider hover:bg-brand-gold transition-colors"
            >
              Browse properties
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AreaGuideDetailPage;
